/**
 * Modular registry describing *hierarchical* content-block structures so one generic editor
 * (`NestedContentEditor`) can render and fully manage any of them — the on-screen deep-dive
 * Scenario ▸ Phase ▸ Section ▸ Question tree being the first consumer.
 *
 * Design goal (per the single-source-of-truth directive): the admin gets complete control of every
 * element of a nested structure — create / edit / reorder / delete at every level — WITHOUT the editor
 * hard-coding any particular hierarchy. A new nested content type is added purely as data here: declare
 * its node types, how each level's children are linked, and defaults for newly-created blocks. The
 * editor code does not change.
 *
 * Two child-link strategies cover the frontier data model exactly (see `resolveContent.ts`):
 *   - `parent`      — children are blocks of the child category whose `parent` === this block's id
 *                     (scenario→phase, section→question).
 *   - `payloadKeys` — children are the blocks of the child category whose `key` is listed, in order, in
 *                     this block's `payload[field]` (an array of keys). Reordering/adding/removing a
 *                     child edits that key list, not a `parent` pointer (phase→section, via
 *                     `scenario_phase.payload.sections`).
 *
 * Per-node editable fields are NOT redeclared here — the editor reuses `CATEGORY_SCHEMAS[category]`
 * (the same friendly, per-field editors the flat content panel uses, including the banded question
 * editor), plus the block's own `label` (the on-screen title) and stable `key`.
 */
import type { CampaignContentBlock } from '../../../../support/campaigns';

export type ChildLink =
  | { via: 'parent' }
  | { via: 'payloadKeys'; field: string };

export interface NodeChildRel {
  /** Node type key of the children. */
  type: string;
  /** How the children are related to this node. */
  link: ChildLink;
  /** Button label for creating a child (defaults to `Add ${childNoun}`). */
  addLabel?: string;
}

export interface NodeTypeDef {
  /** Registry key, referenced by `NestedTreeDef.rootType` and `NodeChildRel.type`. */
  type: string;
  /** The `CampaignContentBlock` category blocks of this node type live in. */
  category: string;
  /** Friendly singular noun ("Scenario", "Phase", "Section", "Question"). */
  noun: string;
  /**
   * Whether this node's on-screen title comes from the block `label`. Almost always true; the tree
   * falls back to `key` when the label is blank.
   */
  titleFromLabel?: boolean;
  /** Child relationships rendered under this node, in order. */
  children?: NodeChildRel[];
  /** Default payload for a newly-created block of this type. */
  defaultPayload?: () => Record<string, unknown>;
  /**
   * Build the stable `key` for a newly-created block. `parent` is the parent block (if any) and
   * `siblingCount` is how many siblings of this type already exist under that parent — enough to
   * produce sensible, unique-ish defaults (the admin can always rename the key afterwards).
   */
  makeKey?: (ctx: { parent?: CampaignContentBlock; siblingCount: number }) => string;
  /** Default label for a newly-created block. */
  makeLabel?: (ctx: { parent?: CampaignContentBlock; siblingCount: number }) => string;
}

export interface NestedTreeDef {
  /** Node type key of the top-level nodes. */
  rootType: string;
  /** Every node type in this tree, keyed by `type`. */
  nodes: Record<string, NodeTypeDef>;
  /** Panel header + sub-header. */
  title: string;
  subtitle: string;
  /** Button label for creating a new root node. */
  addRootLabel: string;
}

/** Short, reasonably-unique key suffix for a freshly-created block (admin-renameable). */
const uid = (): string => Math.random().toString(36).slice(2, 7);

/** The three canonical deep-dive phase ids, used to seed a new phase's key/label by position. */
const PHASE_IDS = ['assess', 'act', 'optimize'];

export const NESTED_CONTENT_TREES: Record<string, NestedTreeDef> = {
  deepdive: {
    rootType: 'scenario',
    addRootLabel: 'Add scenario',
    title: 'Deep-dive scenarios (nested)',
    subtitle:
      'The full Scenario ▸ Phase (Assess / Act / Optimize) ▸ Section ▸ Question tree the on-screen deep-dive console renders — expand any level to edit it, add / reorder / delete at every level. A question’s banded GAP / PARTIAL / COVERED cues and “who to ask” are edited in place. The plays a band unlocks are `CampaignCapability` rows, edited in the Capabilities section. These content blocks save immediately — independently of this step form’s Save/Cancel.',
    nodes: {
      scenario: {
        type: 'scenario',
        category: 'scenario',
        noun: 'Scenario',
        titleFromLabel: true,
        children: [{ type: 'phase', link: { via: 'parent' }, addLabel: 'Add phase' }],
        defaultPayload: () => ({ n: '', sub: '', setup: '' }),
        makeKey: ({ siblingCount }) => `scenario-${siblingCount + 1}-${uid()}`,
        makeLabel: ({ siblingCount }) => `Scenario ${siblingCount + 1}`,
      },
      phase: {
        type: 'phase',
        category: 'scenario_phase',
        noun: 'Phase',
        titleFromLabel: true,
        children: [
          { type: 'section', link: { via: 'payloadKeys', field: 'sections' }, addLabel: 'Add section' },
        ],
        defaultPayload: () => ({ n: '', lead: '', sections: [] }),
        // The runner derives a phase's id from `key.split(':')[1]`, so compose `<scenarioKey>:<phaseId>`
        // and default the phase id to assess/act/optimize by position.
        makeKey: ({ parent, siblingCount }) =>
          `${parent?.key ?? 'scenario'}:${PHASE_IDS[siblingCount] ?? `phase${siblingCount + 1}`}`,
        makeLabel: ({ siblingCount }) =>
          PHASE_IDS[siblingCount]
            ? PHASE_IDS[siblingCount].charAt(0).toUpperCase() + PHASE_IDS[siblingCount].slice(1)
            : `Phase ${siblingCount + 1}`,
      },
      section: {
        type: 'section',
        category: 'scenario_section',
        noun: 'Section',
        titleFromLabel: true,
        children: [{ type: 'question', link: { via: 'parent' }, addLabel: 'Add question' }],
        defaultPayload: () => ({ code: '', group: '', short: '', why: '' }),
        makeKey: ({ siblingCount }) => `section-${siblingCount + 1}-${uid()}`,
        makeLabel: ({ siblingCount }) => `Section ${siblingCount + 1}`,
      },
      question: {
        type: 'question',
        category: 'scenario_question',
        noun: 'Question',
        titleFromLabel: true,
        // A fresh banded question: empty ask/why and the three canonical bands with empty cue/play lists.
        defaultPayload: () => ({
          ask: '',
          why: '',
          who: [],
          options: [
            { value: 'gap', cues: [], plays: [] },
            { value: 'partial', cues: [], plays: [] },
            { value: 'covered', cues: [], plays: [] },
          ],
        }),
        makeKey: ({ siblingCount }) => `q-${siblingCount + 1}-${uid()}`,
        makeLabel: ({ siblingCount }) => `Question ${siblingCount + 1}`,
      },
    },
  },
};
